import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../../../config/constants';

export type CompanyRecord = {
    id?: string | number;
    company_id?: string | number;
    company?: string;
    company_name?: string;
    name?: string;
    address?: string;
    country?: string;
    created_at?: string;
    [key: string]: any;
};

export type RegisterUpdateCompanyPayload = {
    id?: string | number | null;
    company_id?: string | number | null;
    name: string;
    email: string;
    phoneNumber?: string;
    phone_number?: string;
    country: string;
    role?: string;
    role_id?: string | number | bigint | null;
};

function buildJsonHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json' };
}

export function useCompanies() {
    const [companies, setCompanies] = useState<CompanyRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadCompanies = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/api/users/get-all-companies`, {
                method: 'POST',
                headers: buildJsonHeaders(),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch companies');
            }

            const data = await response.json();
            const companyRows: CompanyRecord[] = Array.isArray(data)
                ? data
                : Array.isArray(data.data)
                  ? data.data
                  : [];

            setCompanies(companyRows);
        } catch (err: any) {
            console.error('Error loading companies:', err);
            setError(err.message || 'Failed to load companies');
            setCompanies([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCompanyMainImage = useCallback(async (companyId: string | number | null | undefined) => {
        if (companyId === null || companyId === undefined || String(companyId).trim() === '') return null;

        try {
            const response = await fetch(`${API_BASE}/api/users/get-company-docs`, {
                method: 'POST',
                headers: buildJsonHeaders(),
                body: JSON.stringify({ company_id: companyId }),
            });

            if (!response.ok) throw new Error('Failed to fetch company docs');

            const result = await response.json();
            if (result.success && Array.isArray(result.data) && result.data.length > 0) {
                // Prefer files named exactly `logo` with an extension (logo.png, logo.jpg)
                const logoPattern = /^logo(\.[^.]*)?$/i;
                const logoFile = result.data.find((d: any) => logoPattern.test(String(d.name || '')));
                if (logoFile) {
                    return logoFile;
                }

                // Fallback: first image-type doc
                const imgs = result.data.filter((d: any) => String(d.contentType || '').toLowerCase().startsWith('image/'));
                if (imgs.length > 0) {
                    return imgs[0];
                }

                // No images; return first doc as last resort
                return result.data[0];
            }

            return null;
        } catch (err: any) {
            console.error('[HCompanies] fetchCompanyMainImage error', err);
            return null;
        }
    }, []);

    const registerUpdateCompany = useCallback(
        async (payload: RegisterUpdateCompanyPayload) => {
            const body = {
                company: {
                    id: payload.id ?? payload.company_id ?? null,
                    name: String(payload.name ?? '').trim(),
                    email: String(payload.email ?? '').trim(),
                    phone_number: String(payload.phone_number ?? payload.phoneNumber ?? '').trim(),
                    country: String(payload.country ?? '').trim(),
                    role_id: payload.role_id ?? null,
                },
            };

            const response = await fetch(`${API_BASE}/api/users/register-update-company`, {
                method: 'POST',
                headers: buildJsonHeaders(),
                body: JSON.stringify(body),
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to register company');
            }

            await loadCompanies();
            return result;
        },
        [loadCompanies]
    );

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    return {
        companies,
        loading,
        error,
        loadCompanies,
        registerUpdateCompany,
        fetchCompanyMainImage,
    };
}

export const useHCompanies = useCompanies;